import { useCallback, useState } from 'react';
import type { ColumnDef } from '../pages/projects/projectsColumns';

/**
 * Per-grid manual column-width overrides, persisted to localStorage.
 *
 * Added for BUG-2026-0183 (Work Orders table — long names were being cut off
 * with no way to widen the column). Opt-in: only grids that render a resizable
 * DataTable use this. Widths are keyed by column `field` so they survive column
 * show/hide and reordering. Kept separate from the `moftakilab:columns:`
 * visibility prefs so a visibility-version purge never wipes user widths.
 */
const STORAGE_PREFIX = 'moftakilab:colwidths:';

/** Smallest a column can be dragged to (keeps the header/handle usable). */
export const MIN_COLUMN_WIDTH = 60;

/** Fallback starting width for a flex column that has no explicit `width`. */
const FLEX_BASE_WIDTH = 160;

/** Concrete starting width for a column before the user has resized it. */
export const defaultColumnWidth = (col: ColumnDef): number =>
  col.width ?? (col.flex ? Math.round(col.flex * FLEX_BASE_WIDTH) : FLEX_BASE_WIDTH);

type WidthMap = Record<string, number>;

export interface UseColumnWidthsResult {
  /** Effective width for a column (user override, else its default). */
  getWidth: (col: ColumnDef) => number;
  /** Persist a new width for a field (clamped to MIN_COLUMN_WIDTH). */
  setWidth: (field: string, width: number) => void;
  /** Whether the user has any saved overrides for this grid. */
  hasOverrides: boolean;
  /** Clear all overrides for this grid (back to the column defaults). */
  reset: () => void;
}

export function useColumnWidths(gridId: string): UseColumnWidthsResult {
  const storageKey = `${STORAGE_PREFIX}${gridId}`;

  const load = (): WidthMap => {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? (JSON.parse(raw) as unknown) : null;
      return parsed && typeof parsed === 'object' ? (parsed as WidthMap) : {};
    } catch {
      return {};
    }
  };

  const [widths, setWidths] = useState<WidthMap>(load);

  const persist = useCallback(
    (next: WidthMap) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* ignore quota / disabled storage — widths just won't persist */
      }
    },
    [storageKey],
  );

  const getWidth = useCallback(
    (col: ColumnDef): number => widths[col.field] ?? defaultColumnWidth(col),
    [widths],
  );

  const setWidth = useCallback(
    (field: string, width: number) => {
      setWidths((prev) => {
        const next = { ...prev, [field]: Math.max(MIN_COLUMN_WIDTH, Math.round(width)) };
        persist(next);
        return next;
      });
    },
    [persist],
  );

  const reset = useCallback(() => {
    setWidths({});
    try {
      localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  return { getWidth, setWidth, hasOverrides: Object.keys(widths).length > 0, reset };
}
